import React from 'react';
import clsx from 'clsx';
import { Badge } from '@dopomogai/ui';
import { ArrowRight } from 'lucide-react';
import { ArticleSummary } from '@dopomogai/types'; 
import { useAnalytics } from '@dopomogai/agent-kit';
import { ArticleTeaserCard } from './ArticleTeaserCard';

interface ArticlesTeaserSectionProps {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  articles: ArticleSummary[];
  cta?: { label: string; actionId: string };
  onAction?: (id: string, payload?: any) => void;
  className?: string;
  analyticsSource?: string;
}

export const ArticlesTeaserSection: React.FC<ArticlesTeaserSectionProps> = ({
  eyebrow,
  title,
  subtitle,
  articles,
  cta,
  onAction,
  className,
  analyticsSource,
}) => {
  const analytics = useAnalytics();
  if (!articles || articles.length === 0) return null;

  const handleArticleClick = (slug: string) => {
    const article = articles.find((item) => item.slug === slug);
    if (article && analyticsSource) {
      analytics.track('article_card_open', {
        source: analyticsSource,
        article_id: article.id,
        article_slug: article.slug,
        article_route: article.route || `/blog/${article.slug}`,
        article_category: article.category,
        article_publish_date: article.publishDate,
        article_read_time_minutes: article.readTimeMinutes,
      });
    }
    onAction?.('OPEN_ARTICLE', { slug, path: article?.route || `/blog/${slug}` });
  };

  const handleViewAllClick = () => {
    // Pass the actionId defined in props. 
    // If strictly meant for "view all", we pass path, otherwise just the ID.
    onAction?.(cta?.actionId || 'VIEW_ALL_ARTICLES', { 
      actionId: cta?.actionId,
      path: '/blog' 
    });
  };

  return (
    <section className={clsx('py-16 md:py-24 bg-white relative', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        {(eyebrow || title || subtitle) && (
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 md:mb-16">
            <div className="max-w-3xl">
              {eyebrow && (
                <div className="mb-4">
                  <Badge text={eyebrow} accentColor="emerald" />
                </div>
              )}
              {title && (
                <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-6 text-xl text-slate-600 leading-relaxed max-w-2xl">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article) => (
            <ArticleTeaserCard
              key={article.slug} 
              article={article}
              onClick={() => handleArticleClick(article.slug)}
            />
          ))}
        </div>

        {/* Bottom CTA Button */}
        {cta && (
          <div className="mt-16 flex justify-center">
            <button
              onClick={handleViewAllClick}
              className={clsx(
                "relative inline-flex items-center justify-center gap-3 px-8 py-4",
                "bg-white border-2 border-black rounded-xl",
                "text-xl font-extrabold text-slate-900",
                "shadow-none",
                "hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]",
                "active:translate-y-0 active:translate-x-0 active:shadow-none",
                "transition-all duration-200 ease-out"
              )}
            >
              {cta.label} <ArrowRight size={24} strokeWidth={3} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
